import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import {
  useGenerateSuggestedOpener,
  useMarkNotificationAsRead,
  useWhatToWorkOnNotification,
} from "@/lib/api/notifications";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import { useMessageInputStore } from "@/lib/hooks/conversations/use-message-input";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import React, { useState } from "react";

interface WhatToWorkOnProps {
  mentorshipId: string;
}

export const WhatToWorkOn: React.FC<WhatToWorkOnProps> = ({ mentorshipId }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [suggestedOpener, setSuggestedOpener] = useState<string | null>(null);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  const { data, isLoading } = useWhatToWorkOnNotification(mentorshipId);
  const generateOpener = useGenerateSuggestedOpener();
  const markAsRead = useMarkNotificationAsRead();
  const { setMessageInput } = useMessageInputStore();
  const { currentConversation } = useCurrentConversation();

  const notification = data?.notification;

  if (isLoading || !notification) {
    return null;
  }

  const handleGenerateOpener = async () => {
    try {
      const result = await generateOpener.mutateAsync(notification.id);
      setSuggestedOpener(result.suggestedOpener);

      toast({
        title: "Suggested opener generated!",
        description: "The conversation starter is ready for you to use.",
      });
    } catch (error) {
      toast({
        title: "Error generating opener",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleMarkAsRead = async () => {
    try {
      await markAsRead.mutateAsync(notification.id);

      // Invalidate the what-to-work-on query to hide the component immediately
      queryClient.invalidateQueries({
        queryKey: ["what-to-work-on", mentorshipId],
      });

      toast({
        title: "Marked as complete",
        description: "This goal has been marked as addressed.",
      });
    } catch (error) {
      toast({
        title: "Error marking as read",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleOpenerClick = () => {
    if (suggestedOpener && currentConversation) {
      setMessageInput(currentConversation, suggestedOpener);
      toast({
        title: "Opener added to message",
        description:
          "The suggested opener has been added to your message input.",
      });
    }
  };

  return (
    <Card className="w-full border-0 shadow-none">
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-gray-900">What to work on</h2>
          <Badge className="bg-green-100 px-3 py-1 text-xs font-medium text-green-800 hover:bg-green-100">
            New teen answer
          </Badge>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <div className="space-y-4">
            <p className="text-base leading-relaxed text-gray-700">
              &ldquo;{notification.data?.originalResponse}&rdquo;
            </p>

            {notification.data?.enhancedResponse && (
              <div className="flex flex-col gap-2">
                <div
                  className="flex cursor-pointer items-center gap-1 text-sm text-gray-600"
                  onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                >
                  AI Summary
                  {isSummaryExpanded ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </div>
                {isSummaryExpanded && (
                  <p className="rounded-md border bg-gray-50 p-2 text-sm text-gray-700">
                    {notification.data.enhancedResponse}
                  </p>
                )}
              </div>
            )}

            {suggestedOpener && (
              <div
                className="cursor-pointer rounded-md bg-gray-50 p-2 transition-colors hover:bg-gray-100"
                onClick={handleOpenerClick}
              >
                <p className="mb-2 text-sm font-medium text-gray-900">
                  Suggested opener
                </p>
                <p className="text-sm leading-relaxed text-gray-700">
                  {suggestedOpener}
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleGenerateOpener}
                //TODO: Remove this true once we validate that the opener is good
                disabled={true}
                size="sm"
                className="flex-1"
              >
                {generateOpener.isPending ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : suggestedOpener ? (
                  "Regenerate opener"
                ) : (
                  "Generate opener"
                )}
              </Button>

              <Button
                onClick={handleMarkAsRead}
                variant="outline"
                size="sm"
                className="flex-1"
                disabled={markAsRead.isPending}
              >
                {markAsRead.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Mark as complete
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
